import React, { memo, useCallback } from 'react';
import { useRouter } from 'expo-router';
import { Modal, StyleSheet, View } from 'react-native';

import { IconLink, LoadingAnimation, theme, TransparentModal, useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTrackEventEffect } from '@order/Telemetry';

import { LocationSearch, PageWrapper, PermissionsPrimer } from '../../components';
import { useLocationPermissionPrimer, usePushNotificationPermissionPrimer } from '../../hooks';
import { useReorderState } from '../ReorderingScreen';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationsScreen = memo(() => {
  const { currentBreakpoint } = useResponsive();
  const geofencingPrimerVariant = useFeatureFlag('CELS-5256-geofencing-primer-variant');
  const {
    reordering: isReordering,
    restaurantSlug: reorderRestaurantSlug,

    deliveryOrderDetail,
  } = useReorderState();

  const pushPrimer = usePushNotificationPermissionPrimer({
    variants: LOCATIONS_SCREEN_PUSH_VARIANTS,
  });
  const locationPrimer = useLocationPermissionPrimer({
    variants: LOCATIONS_SCREEN_LOCATION_VARIANTS,
  });

  const isLoading =
    pushPrimer.isPushNotificationPermissionsLoading || locationPrimer.isLocationPermissionsLoading;
  const isPrimerActive = pushPrimer.isPrimerActive || locationPrimer.isPrimerActive;

  const interactedLocationRestaurantSlug = isReordering ? reorderRestaurantSlug : undefined;
  const interactedLocationDeliveryAddressId = isReordering
    ? deliveryOrderDetail?.addressId
    : undefined;

  const shouldRequestLocationPermission = !(
    locationPrimer.isLocationPermissionDenied ||
    locationPrimer.isLocationPermissionSkipped ||
    locationPrimer.isLocationPermissionAlreadyShown
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventEffect({
    name: 'location.view',
    payload: { variant: geofencingPrimerVariant },
  });

  // ─────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return <LoadingAnimation withNavbarOffset backgroundColor={theme.colors.APP_BACKGROUND} />;
  }

  if (isPrimerActive) {
    return (
      <Modal visible>
        <PermissionsPrimer pushPrimer={pushPrimer} locationPrimer={locationPrimer} />
      </Modal>
    );
  }

  if (currentBreakpoint.isXS) {
    return (
      <View style={styles.wrapper}>
        <LocationSearch
          interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
          interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
          shouldRequestLocationPermission={shouldRequestLocationPermission}
        />
      </View>
    );
  }

  return (
    <PageWrapper contentContainerStyle={styles.wrapper}>
      <LocationSearch
        interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
        interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
      />
    </PageWrapper>
  );
});

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSelectionModalScreen = () => {
  const router = useRouter();
  const { currentBreakpoint } = useResponsive();

  const {
    reordering: isReordering,
    restaurantSlug: reorderRestaurantSlug,
    deliveryOrderDetail,
  } = useReorderState();

  // ─── Flags ─────────────────────────────────────────────────────────────────

  const shouldShowModalCloseIcon = !currentBreakpoint.isXS;

  // ─── Derived ───────────────────────────────────────────────────────────────

  const interactedLocationRestaurantSlug = isReordering ? reorderRestaurantSlug : undefined;
  const interactedLocationDeliveryAddressId = isReordering
    ? deliveryOrderDetail?.addressId
    : undefined;

  // ─── Styles ────────────────────────────────────────────────────────────────

  const modalOuterContainerStyles = [
    styles.modalOuterContainer,
    shouldShowModalCloseIcon ? styles.modalOuterContainerSM : undefined,
  ];
  const modalInnerContainerStyles = [
    styles.modalInnerContainer,
    shouldShowModalCloseIcon ? styles.modalInnerContainerSM : undefined,
  ];

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const dismissModal = useCallback(() => {
    if (router.canGoBack()) {
      router.back();

      return;
    }

    router.dismissTo('/');
  }, [router]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal
      size="large"
      dialogMaxWidth={DIALOG_MAX_WIDTH}
      dialogMaxHeight={DIALOG_MAX_HEIGHT}
      dialogStyles={styles.modalDialog}
      onClose={dismissModal}
      transparent
      withBackdrop
    >
      <View style={modalOuterContainerStyles}>
        {shouldShowModalCloseIcon ? (
          <IconLink
            name="IconClose"
            palette="white"
            width={40}
            height={40}
            onPress={dismissModal}
            style={styles.modalCloseIcon}
          />
        ) : undefined}

        <View style={modalInnerContainerStyles}>
          <LocationSearch
            interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
            interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
            isInsideModal={true}
            onNavClose={dismissModal}
          />
        </View>
      </View>
    </TransparentModal>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  modalDialog: {
    shadowOpacity: 0, // override default styles
    shadowRadius: 0,
    elevation: 0,
  },
  wrapper: {
    height: '100%',
  },
  modalOuterContainer: {
    flex: 1,
    gap: theme.spacing['4'],
  },
  modalOuterContainerSM: {
    padding: theme.spacing['6'],
  },
  modalInnerContainer: {
    flex: 1,
    overflow: 'hidden',
  },
  modalInnerContainerSM: {
    borderRadius: theme.radius.xlarge,
    boxShadow: `3px 3px 32px -10px ${theme.colors.OPACITY.DARK_KALE.DARK}`,
  },
  modalCloseIcon: {
    marginLeft: 'auto',
    boxShadow: `3px 3px 32px -10px ${theme.colors.OPACITY.DARK_KALE.DARK}`,
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const DIALOG_MAX_WIDTH = 1280;
const DIALOG_MAX_HEIGHT = 870;

const LOCATIONS_SCREEN_LOCATION_VARIANTS: LocationPrimerVariants = ['B', 'C'];
const LOCATIONS_SCREEN_PUSH_VARIANTS: PushPrimerVariants = ['B'];

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationPrimerVariants = Parameters<typeof useLocationPermissionPrimer>[0]['variants'];
type PushPrimerVariants = Parameters<typeof usePushNotificationPermissionPrimer>[0]['variants'];
